// @flow
import * as React from 'react'
import styled from 'styled-components'
import uniqid from 'uniqid'
import { Input, ErrorMessage } from './'
import { Colors } from '../Colors'
import { RobotoMedium } from '../Typography/Fonts'

const LabelWrapper = styled.div`
  position: relative;
  background: transparent;

  & ${Input} {
    margin-top: 18px;
    margin-bottom: 10px;
  }

  & input::placeholder {
    color: ${({ linkedInputIsFocused, linkedInputIsEmpty }) =>
      !linkedInputIsFocused || (linkedInputIsFocused && !linkedInputIsEmpty)
        ? 'transparent'
        : Colors.greyRegular};

    transition: color ease-in-out 200ms;
  }
`

const LabelInput = styled.label`
  position: absolute;
  top: ${({ floating }) => (floating ? '2px' : '18px')};
  left: 0;
  padding: 0;
  font-family: ${RobotoMedium.fontFamily};
  font-weight: ${RobotoMedium.weight};
  font-size: ${({ floating }) => (floating ? '12px' : '18px')};
  color: ${Colors.white30};
  text-transform: lowercase;
  z-index: 1;
  transition: ${({ animateTransition }) =>
    animateTransition ? 'all ease-in-out 200ms' : null};

  &:hover {
    cursor: text;
  }

  &:after {
    opacity: ${({ floating }) => (floating ? 1 : 0)};
    content: '${({ message }) => message}';
    font-family: ${RobotoMedium.fontFamily};
    font-weight: ${RobotoMedium.weight};
    font-size: 12px;
    color: ${Colors.red};

    transition: opacity ease-in-out 200ms;
  }
`

const FloartingErrorMessage = styled(ErrorMessage)`
  position: absolute;
  opacity: ${({ floating }) => (floating ? 0 : 1)};
  top: 0px;
  left: 0px;
  padding: 0px;
  z-index: 1;
  text-transform: lowercase;

  transition: ${({ animateTransition }) =>
    animateTransition ? 'opacity ease-in-out 100ms' : null};
`

const ErrorMessageSeparator = styled.span`
  opacity: ${({ isVisible }) => (isVisible ? 1 : 0)};
  margin: 0 3px;
  color: ${Colors.red};
  transition: ${({ animateTransition }) =>
    animateTransition ? 'opacity ease-in-out 200ms' : null};
`

const FloatingLabel = ({
  label,
  errorMessage,
  children,
  ...props
}: {
  label: React.Node,
  errorMessage: ?string,
  children: React.Node,
}): React.Node => {
  const originalInput = React.Children.only(children)

  const {
    id,
    placeholder,
    value,
    defaultValue,
    errored = false,
    onChange,
    onFocus,
    onBlur,
  } = originalInput.props

  const [inputId] = React.useState(id || `input-${uniqid()}`)
  const [isMounted, setIsMounted] = React.useState(false)
  const [inputIsFocused, setInputIsFocused] = React.useState(false)
  const [inputIsEmpty, setInputIsEmpty] = React.useState(
    !value && !defaultValue,
  )

  React.useEffect(() => {
    if (isMounted) {
      return
    }

    setIsMounted(true)
  }, [isMounted])

  React.useEffect(() => {
    setInputIsEmpty(!value && !defaultValue)
  }, [value, defaultValue])

  const inputElement = React.cloneElement(originalInput, {
    id: inputId,

    ...(placeholder ? { placeholder: ' ' } : null),

    onChange: (e, ...args) => {
      setInputIsEmpty(!e.target.value)

      if (onChange) {
        onChange(e, ...args)
      }
    },

    onFocus: (e, ...args) => {
      setInputIsFocused(true)

      if (onFocus) {
        onFocus(e, ...args)
      }
    },

    onBlur: (e, ...args) => {
      setInputIsFocused(false)

      if (onBlur) {
        onBlur(e, ...args)
      }
    },
  })

  const isFloating = () => inputIsFocused || !inputIsEmpty
  const hasErrorMessage = () => !!errorMessage
  const isAnimationActivated = () => isMounted

  return (
    <LabelWrapper
      linkedInputIsEmpty={inputIsEmpty}
      linkedInputIsFocused={inputIsFocused}
    >
      <LabelInput
        htmlFor={inputId}
        floating={isFloating()}
        animateTransition={isAnimationActivated()}
        linkedInputIsFocused={inputIsFocused}
        linkedInputIsErrored={errored}
        message={errorMessage}
      >
        {label}

        <ErrorMessageSeparator
          isVisible={isFloating() && hasErrorMessage()}
          animateTransition={isAnimationActivated()}
        >
          |
        </ErrorMessageSeparator>
      </LabelInput>

      <FloartingErrorMessage
        floating={isFloating()}
        animateTransition={isAnimationActivated()}
      >
        {errorMessage}
      </FloartingErrorMessage>

      {inputElement}
    </LabelWrapper>
  )
}

export default FloatingLabel
